import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInert } from '../hooks/useFocusTrap';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useShortcuts } from '../hooks/useShortcuts';
import { useEdgeSwipeOpen } from '../hooks/useSwipeToClose';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { LayoutContext } from '../lib/layout';
import { Sidebar } from './Sidebar';

/**
 * Authenticated layout: sidebar + main. On desktop the sidebar
 * is a static 260 px rail. On mobile it becomes a fixed drawer
 * behind a scrim; page headers trigger it via LayoutContext.
 */
export function AppShell() {
  const { status } = useAuth();
  const navigate = useNavigate();
  useShortcuts();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);
  const isMdUp = useMediaQuery('(min-width: 768px)');
  const drawerOpen = sidebarOpen && !isMdUp;
  const mainRef = useRef<HTMLElement>(null);
  useInert(mainRef, drawerOpen);
  useEdgeSwipeOpen(!isMdUp && !sidebarOpen, openSidebar);

  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeSidebar();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, closeSidebar]);

  // Close the drawer automatically on every navigation so tapping
  // "Settings" in the drawer doesn't leave it hanging open over
  // the destination page.
  const location = useRouterState({ select: (s) => s.location.pathname });
  // Close on pathname change. `location` is the trigger, not a capture.
  // biome-ignore lint/correctness/useExhaustiveDependencies: route identity
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  // Soft page change: fade the new route in without remounting it.
  // Only top-level sections animate; switching chats stays instant.
  const section = location.split('/')[1] ?? '';
  const firstSection = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: section is the trigger
  useEffect(() => {
    if (firstSection.current) {
      firstSection.current = false;
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    mainRef.current?.animate(
      [
        { opacity: 0, transform: 'translateY(6px)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
  }, [section]);

  useEffect(() => {
    if (status === 'anonymous') {
      navigate({ to: '/login', replace: true });
    }
  }, [status, navigate]);

  // iOS can both shrink and pan the viewport when the keyboard opens.
  // Match its height and offset so the composer stays in the visible area.
  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;
    const sync = (): void => {
      const h = vv?.height ?? window.innerHeight;
      root.style.setProperty('--mc-app-h', `${Math.round(h)}px`);
      root.style.setProperty('--mc-app-top', `${Math.round(vv?.offsetTop ?? 0)}px`);
    };
    sync();
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    return () => {
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      root.style.removeProperty('--mc-app-h');
      root.style.removeProperty('--mc-app-top');
    };
  }, []);

  const ctx = useMemo(
    () => ({ sidebarOpen, openSidebar, closeSidebar, toggleSidebar }),
    [sidebarOpen, openSidebar, closeSidebar, toggleSidebar],
  );

  if (status === 'loading') {
    return (
      <div className="mc-app-h flex items-center justify-center">
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    );
  }

  if (status !== 'authenticated') {
    return null;
  }

  return (
    <LayoutContext.Provider value={ctx}>
      <div className="mc-app-h relative top-[var(--mc-app-top,0px)] flex min-h-0 overflow-hidden">
        <Sidebar
          open={sidebarOpen}
          onClose={closeSidebar}
          inactive={!isMdUp && !sidebarOpen}
          trapFocus={drawerOpen}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="Close menu"
          aria-hidden={!drawerOpen || undefined}
          onClick={closeSidebar}
          className={cn(
            'fixed inset-0 z-30 bg-background/60 backdrop-blur-sm transition-[opacity,visibility] duration-300 md:hidden',
            drawerOpen ? 'visible opacity-100' : 'invisible opacity-0',
          )}
        />
        <main
          ref={mainRef}
          className={cn(
            // Phones: the page slides aside with the drawer, as in native chat apps.
            'mc-drawer-motion flex min-h-0 min-w-0 flex-1 flex-col bg-background',
            drawerOpen && 'translate-x-[min(86vw,340px)]',
          )}
        >
          <Outlet />
        </main>
      </div>
    </LayoutContext.Provider>
  );
}
